// @flow

import React, { type Node } from 'react';
import {
  Body,
  Content,
  PaymentInfoWrapper,
} from '../../../organisms/shared-styled-components';
import styled from 'styled-components';
import { MerchantLogo } from '../../../organisms/merchant-info';
import { MobileTotalAmount } from '../../../organisms/mobile-total-amount';
import {
  getCountry,
  getWithholdTaxes as getWithholdTaxesConfig,
  usePendingIncomingPayment,
} from '@tectonics/prs/incoming-payment';
import {
  type WithholdTaxes,
  getWithholdTaxes as getWithholdTaxesParams,
  formatWithholdTaxesTitleText,
  formatWithholdTaxesInstructionTextWithAmount,
  formatWithholdTaxesDocumentText,
} from '@tectonics/prs/argentina/withhold-taxes';
import { getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getMoneyFormat } from '@tectonics/look-and-feel/shopify/commons/money-format';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { translate } from '@tectonics/translate';

import iconBanckUser from './images/bank-user.svg';
import iconSmartphoneClock from './images/smartphone-clock.svg';
import iconSmartphonePayment from './images/smartphone-payment.svg';
import iconIdea from './images/idea.svg';
import {getExpireDateMessageFromPayment} from '@tectonics/prs/incoming-payment/get-expiration-datetime';

const RedirectPageContent = React.lazy(() => import('../redirect-page'));

export function DebinWithholdTaxesPage(): Node {
  return <DebinRedirectContent />;
}

export function DebinRedirectContent(): Node {
  const incomingPayment = usePendingIncomingPayment();
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(getCountry(incomingPayment));
  const formatAmount = amountToCurrencyString(decimalFormat);
  const amount = formatAmount(getReceiptAmountLocalTotal(receipt));
  const withholdTaxesParams = getWithholdTaxesParams(incomingPayment, amount);
  const pageTexts = debinPageTexts(withholdTaxesParams);
  const expireDateText = getExpireDateMessageFromPayment(incomingPayment);
  const withholdTaxesConfig = getWithholdTaxesConfig(incomingPayment);
  const isWithholdByCustomer = withholdTaxesConfig === 'not_withhold_taxes';

  if (incomingPayment['redirect_url'] === undefined) {
    return (
      <Content>
        <MerchantLogo height="100px" />
        <MobileTotalAmount />
        <Body>
          <PaymentTitle>
            {pageTexts.titleText}
          </PaymentTitle>
          <PaymentInfoWrapper>
            <PaymentSubtitle>
              {pageTexts.subtitleText}
            </PaymentSubtitle>

            <PaymentInstruction image={iconBanckUser} text={pageTexts.instructionTextWithAmount} />
            <PaymentInstruction image={iconSmartphoneClock} text={pageTexts.instructionTextInstruction} />
            <PaymentInstruction image={iconSmartphonePayment} text={expireDateText} />
          </PaymentInfoWrapper>

          <PaymentHelp>
            <HelpIcon src={iconIdea} />
            <HelpText dangerouslySetInnerHTML={{__html: pageTexts.helpText}} />
          </PaymentHelp>

          {isWithholdByCustomer && <PaymentDocumentation>
            {pageTexts.documentText}
          </PaymentDocumentation>}
        </Body>
      </Content>
    );
  } else {
    return <RedirectPageContent />;
  }
}

type PaymentInstructionProps = {
  image: string,
  text: string,
};

function PaymentInstruction({ image, text }: PaymentInstructionProps): Node {
  return (
    <InstructionContainer>
      <InstructionIcon src={image} />
      <InstructionText>{text}</InstructionText>
    </InstructionContainer>
  );
}

type DebinPageTexts = {
  titleText: string,
  subtitleText: string,
  instructionTextWithAmount: string,
  instructionTextInstruction: string,
  instructionTextExpiryTime: string,
  helpText: string,
  documentText: string,
};

function debinPageTexts(withholdTaxesParams: WithholdTaxes): DebinPageTexts {
  const debinTranslatePath = 'volcanes.payment-methods.banktransfer.debin';

  const pageTexts: DebinPageTexts = {
    titleText: formatWithholdTaxesTitleText(withholdTaxesParams),
    subtitleText: translate(`${debinTranslatePath}.subtitleText`),
    instructionTextWithAmount: formatWithholdTaxesInstructionTextWithAmount(withholdTaxesParams),
    instructionTextInstruction: translate(`${debinTranslatePath}.instructionTextInstruction`),
    instructionTextExpiryTime: translate(`${debinTranslatePath}.instructionTextExpiryTime`),
    helpText: translate(`${debinTranslatePath}.helpText`),
    documentText: formatWithholdTaxesDocumentText(withholdTaxesParams),
  };

  return pageTexts;
}

const PaymentTitle = styled.div`
  padding: 32px 16px;
  margin-bottom: 12px;
  font-size: 1.8em;
  font-family: ${props => props.theme.fontFamily.display};
`;
const PaymentSubtitle = styled.div`
  width: 100%;
  padding: 24px 0 16px;
  display: flex;
  justify-content: center;
  font-size: 1em;
  font-weight: bold;
`;
const InstructionContainer = styled.div`
  padding: 16px 16px;
  display: flex;
  &:last-child {
    margin-bottom: 16px;
  }
`;
const InstructionIcon = styled.img`
  display: flex;
  align-items: center;
`;
const InstructionText = styled.span`
  padding: 0 0 0 16px;
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 1.1em;
  font-family: ${props => props.theme.fontFamily.display};
`;
const PaymentHelp = styled.div`
  margin: 32px 0 16px;
  padding: 20px 16px 20px 16px;
  display: flex;
  background-color: #e5eeff;
`;
const HelpIcon = styled.img`
  height: 100%;
`;
const HelpText = styled.div`
  padding-left: 16px;
  text-align: justify;
  font-size: 0.9em;
`;
const PaymentDocumentation = styled.div`
  padding: 16px 0 16px;
  font-size: 0.8em;
`;
